import * as React from 'react';
import { useEffect } from 'react';
import {
  Box,
  Button,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Modal,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from '@mui/material';
import KpiComponent from '@/components/Base/KpiComponent.tsx';
import { Editor } from 'primereact/editor';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import useModalStore from '@/features/responsible/hooks/useModal.tsx';
import { CardModel, ColumnModel, CommentModel, CrmType, ResponsibleCardModel } from '@/features/crm/types';
import { useDetailOccupancy } from '@/features/occupancyDashboard/api/getDetailOccupancy.ts';
import Step from '@/components/Base/Step.tsx';
import { createCard } from '@/features/crm/api/createCard.ts';
import { enqueueSnackbar } from 'notistack';
import useLoadingActions from '@/hooks/useLoadingActions.tsx';
import { createComment } from '@/features/crm/api/createComment.ts';
import { updateCard } from '@/features/crm/api/changeCard.ts';
import { useListColumns } from '@/features/crm/api/listColumns.ts';
import ListComments from '@/features/crm/ListComments.tsx';
import { SerieModel } from '@/features/list-serie/types';
import { extractBackendErrors } from '@/utils/extractBackEndError';
import getApi from '@/lib/axios';
import { TypesCRM } from '@/types/crm';
import { QueryKey, useQueryClient } from '@tanstack/react-query';

type AddCardResponsibleProps = {
  handleClose: () => void;
  type: any;
};

const AddCardResponsible: React.FC<AddCardResponsibleProps> = ({ handleClose }) => {
  const {
    responsible,
    columns,
    card: currentCard,
    serieId,
    step,
    type,
    toggleCardResponsible,
  } = useModalStore((state) => state);

  const { refetchQueries } = useQueryClient()

  const { refetch } = useListColumns(type);

  const { data: occupancy } = useDetailOccupancy(serieId);
  const { setIsLoading } = useLoadingActions();

  const style = {
    position: 'absolute' as 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    maxWidth: '900px',
    width: '100%',
    bgcolor: '#fff',
    borderRadius: '20px',
    maxHeight: '90%',

    overflowY: 'hidden',
    scrollbarWidth: 'thin',
    scrollbarColor: '#828282 #fff',
    '&:hover': {
      overflowY: 'auto',
    },
    '&::-webkit-scrollbar': {
      width: '2px',
    },
    '&::-webkit-scrollbar-track': {
      background: '#fff',
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: '#828282',
      borderRadius: '8px',
    },
  };

  const formik = useFormik<CardModel>({
    initialValues: {
      comments: [
        {
          texto: '',
        } as CommentModel,
      ],
    } as unknown as CardModel,
    validationSchema: Yup.object({
      valor_atual: Yup.string().required('o comentário é obrigatória'),
      valor_proposta: Yup.string().required('o comentário é obrigatória'),
      // comments: Yup.array().of(
      //   Yup.object().shape({
      //     texto: Yup.string().required('o comentário é obrigatória'),
      //   }),
      // ),
    }),
    onSubmit: async (values) => {
      try {
        const api = await getApi();

        setIsLoading(true, currentCard ? 'Atualizando cartão...' : 'Cadastrando cartão...');

        let cardId = currentCard?.id;
        if (currentCard) {
          await updateCard(currentCard.id, {
            id: currentCard.id,
            valor_atual: values.valor_atual,
            valor_proposta: values.valor_proposta,
            status: values.status,
          } as CardModel);
        } else {
          const { data: card } = await api.post('/crm/cartoes/', {
            ...values,
            responsavel: responsible.id,
            step: step.id,
            painel_ocupacao: occupancy.id,
          } as CardModel);

          cardId = card.id;
        }

        if (values.comments[0].texto) {
          await createComment({
            card: cardId,
            texto: values.comments[0].texto,
          } as CommentModel);
        }

        enqueueSnackbar(currentCard ? 'Cartão atualizado com sucesso!' : 'Cartão adicionado com sucesso!', {
          variant: 'success',
          anchorOrigin: { vertical: 'top', horizontal: 'center' },
        });

        refetchQueries({ queryKey: 'fetch-columns' as unknown as QueryKey }).then()
        refetchQueries({ queryKey: 'step-columns' as unknown as QueryKey }).then()
        handleClose();

        toggleCardResponsible(false);
      } catch (e) {
        console.dir(e, { depth: null })
        enqueueSnackbar('Capacidade máxima da turma atingida!', {
          variant: 'error',
          anchorOrigin: { vertical: 'top', horizontal: 'center' },
        });
      } finally {
        setIsLoading(false, '');
      }
    },
  });

  useEffect(() => {
    if (currentCard) {
      formik.setFieldValue('valor_atual', currentCard.valor_atual);
      formik.setFieldValue('valor_proposta', currentCard.valor_proposta);
      formik.setFieldValue('status', currentCard.status);
    }
  }, [currentCard]);

  const isCommentError = formik.submitCount && Array.isArray(formik.errors.comments) && formik.errors.comments.length > 0 && typeof formik.errors.comments[0] === 'object';
  const order = columns.filter((c: ColumnModel) => c.id === (currentCard?.step || step.id))[0];
  const serie = (currentCard ? (currentCard.responsavel as ResponsibleCardModel).serie : occupancy?.serie) as SerieModel;

  return (
    <div>
      <Modal
        open={true}
        onClose={handleClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box
          sx={style}
          component={'form'}
          noValidate
          onSubmit={formik.handleSubmit}
        >
          <Box style={{ background: '#93DC3F', borderRadius: '20px 20px 0 0', boxShadow: '0px 4px 4px 0px #00000040' }}>
            <Typography color={'#231F20'} variant="h3" p={'10px 29px'}>
              {responsible.name}
            </Typography>
            <Typography color={'#231F20'} p={'0px 29px'}>
              Série: {serie?.serie_nome} Turma: {serie?.turma?.nome} Turno: {serie?.turno?.nome}
            </Typography>
          </Box>
          <Stack sx={{ p: '20px 30px 50px 30px' }} flexDirection={'row'} justifyContent={'space-between'}>
            <Stack flexDirection={'row'} gap={'10px'}>
              {
                currentCard && formik.values.valor_atual ? (
                    <KpiComponent
                      key={1}
                      title="Valor atual"
                      formik={formik}
                      field={'valor_atual'}
                    />
                  )
                  :
                  <KpiComponent
                    key={2}
                    title="Valor atual"
                    formik={formik}
                    field={'valor_atual'}
                  />
              }

              {
                currentCard && formik.values.valor_proposta ? (
                    <KpiComponent
                      key={3}
                      title="Valor enviado de proposta"
                      formik={formik}
                      field={'valor_proposta'} />
                  )
                  :
                  <KpiComponent
                    key={4}
                    title="Valor enviado de proposta"
                    formik={formik}
                    field={'valor_proposta'} />
              }
            </Stack>

            <Stack flexDirection={'row'} gap={'10px'}>
              <FormControl>
                <RadioGroup
                  name="status"
                  sx={{ flexDirection: 'row' }}
                  onChange={formik.handleChange}
                  value={formik.values.status}
                >
                  <FormControlLabel
                    sx={{ bgcolor: '#15AB51', borderRadius: '24px', color: '#fff', paddingRight: '10px' }}
                    value={1}
                    control={
                      <Radio
                        icon={<CheckBoxOutlineBlankIcon sx={{ color: '#fff' }} />}
                        checkedIcon={<CheckBoxIcon sx={{ color: '#fff' }} />}
                      />
                    }
                    label="Ganho"
                  />
                  <FormControlLabel
                    value={2}
                    control={
                      <Radio
                        icon={<CheckBoxOutlineBlankIcon sx={{ color: '#fff' }} />}
                        checkedIcon={<CheckBoxIcon sx={{ color: '#fff' }} />}
                      />
                    }
                    label="Perdido"
                    sx={{ bgcolor: '#E04A4C', borderRadius: '24px', color: '#fff', paddingRight: '10px' }}
                  />
                </RadioGroup>
              </FormControl>
            </Stack>
          </Stack>
          <Stack sx={{ p: '0px 0px 0px 30px' }} flexDirection={'row'}>
            {
              columns.map((column, index) => {
                if(type === TypesCRM.REMATRICULA)
                  index = index + 4
                else if(type === TypesCRM.INADIMPLENCIA)
                  index = index + 8

                const position = (index === 0 || index === 4 || index === 8) ? 'first' : 'other';

                let latest =
                  (currentCard?.histories ?? [])
                    .filter(h => h.from_step === index + 1)
                    .toSorted((a, b) => Date.parse(b.changed_at) - Date.parse(a.changed_at))[0] ?? null;

                if(index + 1 === (type === TypesCRM.MATRICULA ? 4 : type === TypesCRM.REMATRICULA ? 8 : 12) ) {
                  latest =
                    (currentCard?.histories ?? [])
                      .filter(h => h.to_step === index + 1)
                      .toSorted((a, b) => Date.parse(b.changed_at) - Date.parse(a.changed_at))[0] ?? null;
                }

                return (
                  <Step
                    isActive={column.ordem <= order.ordem}
                    title={column.nome} subtitle={latest?.changed_at ? new Date(latest?.changed_at).toLocaleString('pt-BR', {
                      timeZone: 'America/Fortaleza',
                      month: 'short',
                      day: 'numeric',
                      hour: 'numeric',
                      minute: '2-digit',
                      hour12: false,
                    }) : '-'}
                    position={position}
                    sx={{
                      marginLeft: position === 'first' ? undefined : '-40px',
                    }}
                  />
                );
              })
            }
          </Stack>

          {
            currentCard && <ListComments cardId={currentCard.id} />
          }

          <Box sx={{ p: '20px 30px 25px 30px', width: '100% !important' }} flexDirection={'row'}>
            <Typography color={'#231F20'} mb={'20px'} variant="h3">
              Adicionar um comentário
            </Typography>
            <FormControl fullWidth error={isCommentError}>
              <Editor
                value={formik.values.comments[0].texto}
                onTextChange={(e) => {
                  formik.setFieldValue('comments.0.texto', String(e.htmlValue));
                }}
                style={{ height: '187px' }} />
              {
                !!isCommentError &&
                <FormHelperText>O comentário é obrigatório!</FormHelperText>
              }
            </FormControl>
            <Stack flexDirection={'row'} justifyContent={'end'}>
              <Button
                sx={{ width: '164px', borderRadius: '24px', fontSize: '14px', mt: '10px', bgcolor: '#15AB51' }}
                variant="contained"
                type={'submit'}
              >
                Salvar
              </Button>
            </Stack>
          </Box>
        </Box>
      </Modal>
    </div>
  );
};

export default AddCardResponsible;
